import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Button } from '@mui/material';
import { FormViewer } from 'app/pages/system/forms';
import ConditionTasksTable from 'app/pages/underwriting/conditions/ConditionTasksTable';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import CreateFormModal from 'app/shared/modules/CreateFormModal';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { CompanyType } from 'constants/company';
import { FeatureFlag } from 'constants/featureFlags';
import { LoxDocumentTypeName } from 'constants/forms';
import { NATIONAL_CATEGORY_CODE } from 'constants/states';
import { DataType, LoanTaskFormStatus, TaskActionType } from 'constants/taskCategories';
import { TaskAssignmentType } from 'constants/tasks';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';
import { getPriorToRequestFields } from 'utils/priorTo';
import { replaceValuesInsideCurlyBrackets } from 'utils/string';

import { getLoanSpecificForms } from 'store/forms/actions';
import { closeDialogAction, openDialogAction, showMessageAction } from 'store/global/actions';
import {
  addBrokerConditionAction,
  getLoanBrokerConditionByApplicationIdAction,
  getLoanApplicationTasksAction,
} from 'store/loanApplications/actions';
import {
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';
import { selectTaskTypeDynamicField, selectTaskTypesOptions } from 'store/system/selectors';
import { selectPriorTo } from 'store/underwriting/selectors';

import { FormVariant, formVariantsOptions } from '../../../tasks/Tasks.constants';
import { UnderwritingTestIds } from '../../Underwriting.testIds';

import { getBrokerConditionSchema } from './AddConditionsForm.utils';

const AddBrokerConditionForm = ({
  formRef,
  isLoading,
  setIsLoading,
  customOnAssignFormClick,
  formName,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const taskTypeOptions = useSelector(selectTaskTypesOptions);
  const priorToOptions = useSelector(selectPriorTo);
  const taskTypeDynamicFieldOptions = useSelector(selectTaskTypeDynamicField);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.ADD_CONDITION));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.ADD_CONDITION));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.ADD_CONDITION));

  const isDisabledByLock = isLoading || isPageLocked;

  const loanId = useLoanId();

  const isBrokerTasksEnabled = useFeatureToggle(FeatureFlag.EnableBrokerConditionTaskAssignment);

  const initialFormValues = useMemo(() => {
    return {
      internalText: '',
      borrowerInternalText: '',
      priorToType: '',
      taskTypeIds: [],
    };
  }, []);

  const {
    form,
    isFormChanged,
    setInForm,
    validationErrors,
    setSchema,
    handleChange,
    validateForm,
    validateField,
  } = useForm(initialFormValues);

  useEffect(() => {
    setSchema(
      getBrokerConditionSchema({
        conditionTaskTypes: form.conditionTaskTypes,
        taskTypeDynamicFieldOptions,
        isBrokerTasksEnabled,
      }),
    );
  }, [setSchema, form.conditionTaskTypes, taskTypeDynamicFieldOptions, isBrokerTasksEnabled]);

  const onCreateNewForm = useCallback(
    ({ taskIndex }) => {
      const abortController = new AbortController();

      const onFinishFormUploading = uploadedForm => {
        dispatch(getLoanSpecificForms({ loanId, applicationId }))
          .catch(Error)
          .finally(() => {
            const formParams = {
              name: uploadedForm.name,
              formId: uploadedForm.formId,
              historyEntityId: undefined,
              formType: uploadedForm.formType,
              selectedPage: 1,
            };

            const newForm = _.cloneDeep(form);

            newForm.taskTypeIds[taskIndex].formVariant = formVariantsOptions.find(
              formVariant => formVariant.value === FormVariant.LOAN_SPECIFIC,
            );
            newForm.taskTypeIds[taskIndex].formVariant.formId = uploadedForm.formId;

            const onAssignClick = () => customOnAssignFormClick(newForm);

            dispatch(
              openDialogAction({
                header: 'Create new loan specific form',
                body: dialogProps => (
                  <FormViewer
                    isDisableResetStateAction
                    isNeedCloseButton
                    isShowAssignFormIconButton
                    customOnAssignFormClick={onAssignClick}
                    formParams={formParams}
                    {...dialogProps}
                  />
                ),
                disableEnforceFocus: true,
              }),
            );
          });
      };

      dispatch(
        openDialogAction({
          header: 'Create new loan specific form',
          body: dialogProps => (
            <CreateFormModal
              isDisableResetStateAction
              isLoanSpecificForm
              isPdfOnly
              abortController={abortController}
              applicationId={applicationId}
              defaultDocumentTypeName={LoxDocumentTypeName}
              formAreaCode={NATIONAL_CATEGORY_CODE}
              onFinishFormUploading={onFinishFormUploading}
              {...dialogProps}
            />
          ),
          afterCloseCallback: () => abortController.abort(),
        }),
      );
    },
    [applicationId, customOnAssignFormClick, dispatch, form, loanId],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      const preparedTasks = Object.values(form.conditionTaskTypes || {}).map(taskType => ({
        taskTypeId: taskType.taskTypeId,
        taskAssignment: {
          loanId,
          ...(taskType?.taskAssignmentType === TaskAssignmentType.SLA_GROUP
            ? { type: 'SLA', slaGroupId: taskType.assignee?.value }
            : { type: 'Borrower', borrowerId: taskType.assignee?.value }),
        },
        name: replaceValuesInsideCurlyBrackets({
          string: taskType.name,
          options: taskTypeDynamicFieldOptions,
          keyToReplace: 'name',
          keyToReplaceWith: 'value',
        }),
        description: replaceValuesInsideCurlyBrackets({
          string: taskType.description,
          options: taskTypeDynamicFieldOptions,
          keyToReplace: 'name',
          keyToReplaceWith: 'value',
        }),
        actionType: {
          type: taskType.dataType,
          ...(taskType?.dataType === DataType.FORMS && {
            formBundleId: taskType?.formId?.value,
            isSignatureRequired: taskType?.isSignatureRequired ?? false,
            formStatus: taskType?.formStatus ?? LoanTaskFormStatus.NEW,
            type: TaskActionType.FORM,
          }),
        },
        relationId:
          taskType?.entity?.incomeId || taskType?.entity?.assetId || taskType.entity?.value,
      }));
      dispatch(
        addBrokerConditionAction({
          applicationId,
          loanConditions: [
            {
              type: CompanyType.BROKER,
              internalText: form.internalText?.toString(),
              borrowerInternalText: form.borrowerInternalText?.toString(),
              ...getPriorToRequestFields(form.priorToType),
              tasks: isBrokerTasksEnabled ? preparedTasks : [],
            },
          ],
        }),
      )
        .then(() =>
          Promise.all([
            dispatch(getLoanBrokerConditionByApplicationIdAction({ applicationId })),
            dispatch(getLoanApplicationTasksAction({ applicationId })),
          ]),
        )
        .then(() => {
          dispatch(
            showMessageAction({ message: 'Condition successfully added.', variant: 'success' }),
          );
        })
        .catch(Error)
        .finally(() => {
          dispatch(closeDialogAction());
          setIsLoading(false);
        });
    },
    [
      applicationId,
      dispatch,
      form.borrowerInternalText,
      form.conditionTaskTypes,
      form.internalText,
      form.priorToType,
      isBrokerTasksEnabled,
      loanId,
      setIsLoading,
      taskTypeDynamicFieldOptions,
      validateForm,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <form
      noValidate
      className="flex flex-col justify-center p-24 w-512"
      data-ischanged={isFormChanged}
      data-testid={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM}
      ref={formRef}
      onSubmit={onSubmit}
    >
      <TextFieldCDS
        fullWidth
        multiline
        required
        className="mb-24"
        data-testid={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_INTERNAL_TEXT_INPUT}
        disabled={isDisabledByLock || lockedFieldsMap.internalText}
        error={!!validationErrors.internalText}
        helperText={validationErrors.internalText}
        isHidden={hiddenFieldsMap.internalText}
        label="Internal Text"
        maxRows={1000}
        name="internalText"
        value={form.internalText}
        variant="outlined"
        onChange={handleChange}
      />

      <TextFieldCDS
        fullWidth
        multiline
        className="mb-24"
        data-testid={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_BORROWER_INTERNAL_TEXT_INPUT}
        disabled={isDisabledByLock || lockedFieldsMap.borrowerInternalText}
        error={!!validationErrors.borrowerInternalText}
        helperText={validationErrors.borrowerInternalText}
        isHidden={hiddenFieldsMap.borrowerInternalText}
        label="Borrower Internal Text"
        maxRows={1000}
        name="borrowerInternalText"
        value={form.borrowerInternalText}
        variant="outlined"
        onChange={handleChange}
      />

      <AutocompleteCDS
        fullWidth
        required
        containerClassName="mb-24"
        dataTestIdInput={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_PRIOR_TO_AUTOCOMPLETE_SELECT}
        dataTestIdOption={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_PRIOR_TO_OPTION}
        disabled={isDisabledByLock || lockedFieldsMap.priorToType}
        error={!!validationErrors.priorToType}
        helperText={validationErrors.priorToType}
        isHidden={hiddenFieldsMap.priorToType}
        label="Prior To"
        name="priorToType"
        options={priorToOptions}
        value={form.priorToType}
        variant="outlined"
        withSortedOptions={false}
        onChange={onAutocompleteChange}
      />

      <ConditionTasksTable
        isBrokerType
        form={form}
        formName={formName}
        handleChange={handleChange}
        setInForm={setInForm}
        taskTypeOptions={taskTypeOptions}
        tasks={form.taskTypeIds}
        validateField={validateField}
        validationErrors={validationErrors}
        onCreateNewForm={onCreateNewForm}
      />

      <div className="flex justify-end">
        <Button
          className="whitespace-nowrap"
          data-testid={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_SUBMIT_BUTTON}
          disabled={!isFormChanged || isLoading}
          type="submit"
          variant="contained"
        >
          Add
        </Button>
      </div>
    </form>
  );
};

AddBrokerConditionForm.propTypes = {
  ...WithLoadingPropTypes,
  formRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  formName: PropTypes.string,
};

export default compose(withLoading, memo)(AddBrokerConditionForm);
